import React, { useEffect } from "react";
import { Secondary, Primary, TextLink } from "../../../Button";
import Modal from "../../../Modal";
import { OptionType } from "../../../../types/GeneralTypes";

import { Close } from "../../../Icons";
import { useModalContext } from "../../../../contexts/ModalProvider";
import DatePicker from "../../../DatePicker";

import { breakpoints } from "../../../../styles/variables";
import styles from "./index.module.scss";
import cx from "classnames";
import { analytics } from "../../../../analytics";
import ResultsCount from "../../../ResultsCount";
import Select from "../../../Dropdown/Select";
import { trainsLimit } from "../../../../helpers/pagination";
import { useTrainFiltersContext } from "../../TrainFiltersProvider";
import CheckboxGroup from "../../../CheckboxGroup";
import {
  trainTypeOptions,
  updatesTypeOptions
} from "../../../../helpers/filterOptions";

const MobileFilters = ({
  eventsCount,
  totalEvents
}: {
  eventsCount: number;
  totalEvents: number;
}) => {
  const { setOpenModal, openModal } = useModalContext();
  const {
    selectedPipelines,
    selectedTrainTypes,
    setSelectedTrainTypes,
    setSelectedPipelines,
    selectedUpdatesTypes,
    setSelectedUpdatesTypes,
    applyDateRange,
    updateDateRange,
    resetAllFilters,
    pipelineOptions,
    startDate,
    endDate,
    sortByOptions,
    sortBy,
    setSortBy,
    page
  } = useTrainFiltersContext();
  const modalName = "train-filters";

  const updateValue = (
    value: string,
    selected: Array<string>,
    setSelected: (arg0: Array<string>) => void
  ) => {
    let newValues = [];
    if (selected.includes(value)) {
      newValues = selected.filter(item => item !== value);
    } else {
      newValues = [...selected, value];
    }
    setSelected(newValues);
  };

  const activeFiltersCount = React.useMemo(() => {
    return (
      selectedPipelines.length +
      selectedTrainTypes.length +
      (startDate && endDate ? 1 : 0)
    );
  }, [selectedPipelines, selectedTrainTypes, startDate, endDate]);

  const resetFilters = () => {
    resetAllFilters();
  };

  useEffect(() => {
    const handleResize = () => {
      if (openModal === modalName && window.screen.width > breakpoints.large) {
        setOpenModal("");
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [setOpenModal, openModal]);

  return (
    <div className={styles.Wrapper}>
      <div className={styles.MobileFilterBar}>
        <Secondary
          className={cx(styles.FilterButton, {
            [styles.FilterButtonHasActive]: activeFiltersCount > 0
          })}
          onClick={() => {
            setOpenModal(modalName);
            analytics.historyMobileFiltersOpened();
          }}
        >
          <span className={styles.FilterButtonContent}>
            Filters{" "}
            <span className={styles.FiltersCount}>{activeFiltersCount}</span>
          </span>
        </Secondary>

        <ResultsCount
          totalEvents={totalEvents}
          eventsCount={eventsCount}
          page={page}
          className={styles.ResultsCount}
          limit={trainsLimit}
        />
        <Select
          label={"Sort By:"}
          hideLabel
          items={sortByOptions}
          onChange={(sort: OptionType) => setSortBy(sort.value)}
          initial={
            sortByOptions.find(option => sortBy === option.value) || {
              value: "",
              label: ""
            }
          }
        />
      </div>

      <Modal overlayClassName={styles.Overlay} isOpen={openModal === modalName}>
        <div className={styles.ModalControls}>
          <TextLink
            type="button"
            onClick={() => {
              resetFilters();
            }}
            linkStyle="secondary"
          >
            Clear All
          </TextLink>
          <button
            className={styles.Close}
            type="button"
            onClick={() => setOpenModal("")}
          >
            <Close />
          </button>
        </div>
        <div className={styles.FiltersList}>
          <div className={styles.Filter}>
            <DatePicker
              rangeSelect
              startDate={startDate}
              endDate={endDate}
              onDatesChange={range => {
                updateDateRange(range);
                applyDateRange(range);
              }}
            />
          </div>

          {pipelineOptions.length > 1 ? (
            <div className={styles.Filter}>
              <CheckboxGroup
                label="Pipelines"
                checkboxValue={selectedPipelines}
                onChange={(pipelineName: string) => {
                  updateValue(
                    pipelineName,
                    selectedPipelines,
                    setSelectedPipelines
                  );
                  analytics.historyAddFilter({
                    type: "pipeline",
                    value: pipelineName
                  });
                }}
                name="pipeline-filter"
                options={pipelineOptions}
              />
            </div>
          ) : null}

          <div className={styles.Filter}>
            <CheckboxGroup
              label="Train Types"
              checkboxValue={selectedTrainTypes}
              onChange={(trainType: string) => {
                updateValue(
                  trainType,
                  selectedTrainTypes,
                  setSelectedTrainTypes
                );
                analytics.historyAddFilter({
                  type: "train type",
                  value: trainType
                });
              }}
              name="trainType-filter"
              options={trainTypeOptions}
            />
          </div>

          <div className={styles.Filter}>
            <CheckboxGroup
              label="Updates Types"
              checkboxValue={selectedUpdatesTypes}
              onChange={(updateType: string) => {
                updateValue(
                  updateType,
                  selectedUpdatesTypes,
                  setSelectedUpdatesTypes
                );
                analytics.historyAddFilter({
                  type: "updates type",
                  value: updateType
                });
              }}
              name="updatesType-filter"
              options={updatesTypeOptions}
            />
          </div>

          <Primary
            onClick={() => setOpenModal("")}
            disabled={eventsCount === 0}
            className={styles.ShowButton}
          >
            {eventsCount > 0
              ? `Show ${eventsCount} completed runs`
              : "0 filter results"}
          </Primary>
        </div>
      </Modal>
    </div>
  );
};

export default MobileFilters;
